// Central registry of guided tours. Each entry is keyed by a route prefix.
// Longest-matching prefix wins. Steps use react-joyride semantics — `target`
// is a CSS selector, `body` = centred overlay with no highlight.

const t = (target, title, content, opts = {}) => ({
  target,
  title,
  content,
  placement: 'auto',
  disableBeacon: true,
  ...opts,
});

export const TOURS = {
  // ── Admin HR & Payroll ─────────────────────────────────────────────
  '/admin/hr': {
    id: 'admin-hr',
    title: 'HR Home',
    steps: [
      t('body', 'Welcome to HR',
        'This is the HR command centre. It stitches together your people, salary structures, leave, reimbursements, loans and payroll masters.',
        { placement: 'center' }),
      t('[data-testid="hr-tab-employees"]', 'Employees tab',
        'Your master directory. Admins see every field; HR users see leave / attendance / loans only — bank, PAN and salary data are automatically scrubbed.'),
      t('[data-testid="add-employee-btn"]', 'Add a new employee',
        'One click here creates their login account and their HR profile in the same modal. Send-invite emails a 48-hour set-password link automatically.'),
      t('[data-testid="hr-tab-salary-components"]', 'Salary components',
        'The building blocks of every payslip. Admin-only.'),
      t('[data-testid="hr-tab-earning-heads"]', 'Earning heads',
        'One-off payments outside the recurring salary: bonus, incentive, commission, overtime.'),
      t('[data-testid="hr-tab-reimb-cats"]', 'Reimbursement categories',
        'Categorise reimbursements so payroll picks the right taxable flag and GL mapping.'),
      t('[data-testid="hr-tab-loan-types"]', 'Loan types',
        'Define default interest %, tenure and interest period. Powers paused-loan interest accrual.'),
      t('[data-testid="hr-seed-btn"]', 'Seed defaults',
        'First-time setup: creates 25+ salary components, 5 leave types and the payroll GL mappings so the engine works out of the box.'),
      t('[data-testid="hr-seed-masters-btn"]', 'Seed HR masters',
        'Loads 8 reimbursement categories, 6 loan types and 11 earning heads.'),
    ],
  },
  '/admin/team': {
    id: 'admin-team',
    title: 'Team Dashboard',
    steps: [
      t('body', 'Manager self-service',
        'Everything a manager needs on one page: attendance grid, LWP, leaves used vs quota, open loans, current-quarter review status.',
        { placement: 'center' }),
      t('body', 'What you see',
        'Super admins see every employee. Regular managers see only the people whose manager_user_id matches them.',
        { placement: 'center' }),
    ],
  },
  '/admin/recruitment': {
    id: 'admin-recruitment',
    title: 'Recruitment ATS',
    steps: [
      t('body', 'Six-stage pipeline',
        'Applied → Screening → Interview → Offered → Hired / Rejected. Move candidates with a click.',
        { placement: 'center' }),
      t('[data-testid="new-job-btn"]', 'Job requisitions',
        'Start every hire with a job. The job description drives automatic candidate match-score.'),
      t('[data-testid="new-candidate-btn"]', 'Add a candidate',
        'Paste the resume text. A keyword extractor scores the CV against the job description (0-100).'),
      t('[data-testid="sort-by-score"]', 'Sort by match score',
        'Surfaces the strongest resumes first. Colour-coded badges (green ≥ 70, amber 40-69, red < 40).'),
      t('[data-testid="rescore-all-btn"]', 'Rescore all',
        'Rerun scoring after you tweak a job description or update a resume.'),
    ],
  },
  '/admin/letter-templates': {
    id: 'admin-letter-templates',
    title: 'Letter templates',
    steps: [
      t('body', 'Design your letters',
        'Appointment / rejection / offer / termination / relieving / experience letters — one active template per kind.',
        { placement: 'center' }),
      t('body', 'Merge tokens',
        'Use {{first_name}}, {{job_title}}, {{salary}}, {{joining_date}}, {{employer_name}} — filled at render time.',
        { placement: 'center' }),
      t('body', 'Letterhead PDF',
        'Upload your designed A4 stationery. pypdf overlays the body onto page 1 so the printed output matches your corporate identity.',
        { placement: 'center' }),
    ],
  },
  '/admin/attendance': {
    id: 'admin-attendance',
    title: 'Attendance',
    steps: [
      t('body', 'Monthly grid',
        'Rows are employees, columns are days. Click any cell to mark Present / Absent / Half-day / Leave / Week-off / Holiday.',
        { placement: 'center' }),
      t('[data-testid="att-year"]', 'Pick the period', 'Switch year and month here.'),
      t('[data-testid="att-import-btn"]', 'Biometric CSV import',
        'ZKTeco / ESSL / Matrix / Realtime CSV dumps auto-detected. Manual entries always override imports.'),
      t('body', 'LWP feeds payroll',
        'The LWP tally (Absent + 0.5 × Half-day) is auto-deducted in the payroll run — pro-rating recurring earnings by the missed working-day ratio.',
        { placement: 'center' }),
    ],
  },
  '/admin/attendance-approvals': {
    id: 'admin-attendance-approvals',
    title: 'Attendance approvals',
    steps: [
      t('body', 'The gate before payroll',
        'Payroll Execute refuses to run against an unapproved month. Submit here, another admin approves.',
        { placement: 'center' }),
      t('body', 'Override',
        'Super admins can bypass with ?force=true — the UI will prompt when Execute is blocked.',
        { placement: 'center' }),
    ],
  },
  '/admin/performance': {
    id: 'admin-performance',
    title: 'Performance reviews',
    steps: [
      t('body', 'Auto-aggregated scorecards',
        'Every quarter the platform pulls tasks total/completed/overdue, tickets total/resolved, average ratings and hours logged from CRM.',
        { placement: 'center' }),
      t('body', 'Manager rating',
        'Add star rating (1-5) + a manager comment. Status flows draft → shared → acknowledged.',
        { placement: 'center' }),
    ],
  },
  '/admin/payroll-status': {
    id: 'admin-payroll-status',
    title: 'Payroll status',
    steps: [
      t('body', 'HR follow-up view',
        'After handoff HR loses edit rights but keeps read-only access. Track every run’s lifecycle: draft → executed → handed off → posted.',
        { placement: 'center' }),
    ],
  },
  '/admin/payroll': {
    id: 'admin-payroll',
    title: 'Payroll',
    steps: [
      t('body', 'The engine',
        'Combines salary structure, ad-hoc earnings, approved reimbursements, active loans (skipping paused ones), LWP proration, and slab-based statutory deductions.',
        { placement: 'center' }),
      t('body', 'Handoff to Accounts',
        'Once the run looks correct, click Handoff. HR is locked from further edits; Accounts owns the ledger posting from there.',
        { placement: 'center' }),
    ],
  },
  '/admin/loans': {
    id: 'admin-loans',
    title: 'Loans',
    steps: [
      t('body', 'Amortisation schedule',
        'Click any loan to view the full EMI schedule, outstanding balance and progress bar.',
        { placement: 'center' }),
      t('body', 'Pause / Resume',
        'Paused loans accrue monthly interest at loan-type default_interest_pct ÷ 12 × outstanding. On resume, accrued interest lands on the next payslip as a LOAN_INT_* deduction.',
        { placement: 'center' }),
    ],
  },
  '/admin/tax-slabs': {
    id: 'admin-tax-slabs',
    title: 'Tax slabs',
    steps: [
      t('body', 'Configurable brackets',
        'Country / regime / financial year. Powers TDS, PAYE, NI, PF, EPF, ESI, SOCSO, CESS with standard deduction and slab-based marginal rates.',
        { placement: 'center' }),
      t('body', 'Seed FY 2025-26',
        'One click loads reference slabs for India (Old & New), UK PAYE/NI and Malaysia EPF/SOCSO.',
        { placement: 'center' }),
    ],
  },
  '/admin/statutory-reports': {
    id: 'admin-statutory-reports',
    title: 'Statutory reports',
    steps: [
      t('body', 'One-click regulatory outputs',
        'India: Form 16 · PF ECR · TDS 24Q · PT return. UK: P60 · FPS. Malaysia: EA Form · EPF Form A. Generic: payroll register and loan interest register.',
        { placement: 'center' }),
    ],
  },
  '/admin/accounts-queue': {
    id: 'admin-accounts-queue',
    title: 'Accounts inbox',
    steps: [
      t('body', 'The handoff endpoint',
        'Every payroll run HR has handed off lands here. Drill in, then click Post to close the loop — the run is stamped posted_at.',
        { placement: 'center' }),
      t('body', 'Live totals',
        'Pending count · net awaiting post · lifetime posted count · lifetime net posted.',
        { placement: 'center' }),
    ],
  },

  // ── Emerald Books SaaS ────────────────────────────────────────────
  '/books/app/recurring': {
    id: 'books-recurring',
    title: 'Recurring invoices',
    steps: [
      t('body', 'Set-and-forget',
        'Schedule frequency, next-run date and end conditions. The engine drafts a fresh invoice at every cadence tick.',
        { placement: 'center' }),
    ],
  },
  '/books/app/bank-recon': {
    id: 'books-bank-recon',
    title: 'Bank reconciliation',
    steps: [
      t('body', 'Import → match → clear',
        'Upload the bank statement CSV. Auto-matcher pairs candidates by amount + date; you approve, split or ignore.',
        { placement: 'center' }),
    ],
  },
  '/books/app/statements': {
    id: 'books-statements',
    title: 'Customer statements',
    steps: [
      t('body', 'One click per customer',
        'Pick a date range, generate the statement PDF, email it directly — or bulk-send to every debtor with a balance.',
        { placement: 'center' }),
    ],
  },
  '/books/app/inventory': {
    id: 'books-inventory',
    title: 'Inventory',
    steps: [
      t('body', 'Items & stock',
        'Every item has a SKU, unit, tax mapping and reorder level. Purchase bills raise stock; invoices deplete it.',
        { placement: 'center' }),
    ],
  },
  '/books/app/journals': {
    id: 'books-journals',
    title: 'Journals',
    steps: [
      t('body', 'Every transaction is a journal',
        'Double-entry to the core: for every credit there is an equal debit. Filter by date, account, source module (invoice / bill / payroll).',
        { placement: 'center' }),
    ],
  },
  '/books/app/customers': {
    id: 'books-customers',
    title: 'Customers',
    steps: [
      t('body', 'The AR ledger starts here',
        'Add customers with their billing address, tax id and default currency. Every invoice you raise auto-links to the ledger.',
        { placement: 'center' }),
    ],
  },
  '/books/app/invoices': {
    id: 'books-invoices',
    title: 'Invoices',
    steps: [
      t('body', 'Raise · Send · Get paid',
        'Draft an invoice, add line items (with items from Inventory), pick a tax rate, then send or record a payment against it.',
        { placement: 'center' }),
    ],
  },
  '/books/app/payroll/runs': {
    id: 'books-payroll',
    title: 'Payroll runs (Books)',
    steps: [
      t('body', 'Standalone Books payroll',
        'For SaaS tenants running payroll inside Books. Executes payslips and posts the run to the double-entry ledger in one flow.',
        { placement: 'center' }),
    ],
  },
  '/books/app': {
    id: 'books-dashboard',
    title: 'Books dashboard',
    steps: [
      t('body', 'Welcome to Emerald Books',
        'This dashboard is your daily glance: cash on hand, invoices overdue, bills to pay, top customers by revenue.',
        { placement: 'center' }),
      t('[data-testid="books-nav-invoices"]', 'Invoices',
        'AR lives here — raise, send and reconcile.'),
      t('[data-testid="books-nav-bills"]', 'Bills',
        'AP mirror — enter vendor bills, schedule payments.'),
      t('[data-testid="books-nav-reconciliation"]', 'Reconciliation',
        'Match imported bank statements against your ledger.'),
    ],
  },
};

// Return the tour for the longest matching route prefix, or null.
export const findTourForPath = (pathname) => {
  const keys = Object.keys(TOURS)
    .filter((k) => pathname === k || pathname.startsWith(k + '/') || pathname === k)
    .sort((a, b) => b.length - a.length);
  if (!keys.length) {
    // Also allow direct match with `startsWith` for nested routes
    const alt = Object.keys(TOURS)
      .filter((k) => pathname.startsWith(k))
      .sort((a, b) => b.length - a.length);
    if (!alt.length) return null;
    return TOURS[alt[0]];
  }
  return TOURS[keys[0]];
};
